import "@/App.css";
import {
  createBrowserRouter, createRoutesFromElements, RouterProvider,
  Route, Navigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Layout } from "./components/layout/Layout";
import { TabbedPage } from "./components/layout/TabbedPage";
import { Toaster } from "./components/ui/sonner";
import CapGate from "./components/auth/CapGate";
import { ErrorBoundary, RouteError } from "./components/ErrorBoundary";
import { AI_ASSISTANT_ENABLED } from "./lib/features";

// Pages
import Login from "./pages/Login";
import EmergencyAccess from "./pages/EmergencyAccess";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Items from "./pages/Items";
import ItemDetail from "./pages/ItemDetail";
import Employees from "./pages/Employees";
import EmployeeDetail from "./pages/EmployeeDetail";
import Locations from "./pages/Locations";
import LocationDashboard from "./pages/LocationDashboard";
import Inventory from "./pages/Inventory";
import Requests from "./pages/Requests";
import RequestDetail from "./pages/RequestDetail";
import Vendors from "./pages/Vendors";
import Clients from "./pages/Clients";
import SupervisoryGroups from "./pages/SupervisoryGroups";
import MyAccounts from "./pages/MyAccounts";
import Jobs from "./pages/Jobs";
import Sites from "./pages/Sites";
import Scheduling from "./pages/Scheduling";
import Timesheets from "./pages/Timesheets";
import GeneralLedger from "./pages/GeneralLedger";
import PayrollSAR from "./pages/PayrollSAR";
import PayrollNetPay from "./pages/PayrollNetPay";
import SelfApprovedReport from "./pages/SelfApprovedReport";
import PayRateReviewQueue from "./pages/PayRateReviewQueue";
import HoursTypes from "./pages/HoursTypes";
import EmailDelivery from "./pages/EmailDelivery";
import AlertRecipients from "./pages/AlertRecipients";
import Diagnostics from "./pages/Diagnostics";
import SystemStatus from "./pages/SystemStatus";
import EmployeeMerge from "./pages/EmployeeMerge";
import EvaluationCompletion from "./pages/EvaluationCompletion";
import BillingItems from "./pages/BillingItems";
import PurchaseOrders from "./pages/PurchaseOrders";
import PODetail from "./pages/PODetail";
import Users from "./pages/Users";
import Permissions from "./pages/Permissions";
import EvaluationSettings from "./pages/EvaluationSettings";
import Reports from "./pages/Reports";
import AuditLogs from "./pages/AuditLogs";
import AIAssistant from "./pages/AIAssistant";
import HelpCenter from "./pages/HelpCenter";
import WhatsNew from "./pages/WhatsNew";
import Forecasting from "./pages/Forecasting";
import AIChatWidget from "./components/AIChatWidget";
import AssetCheckouts from "./pages/AssetCheckouts";
import ImportPage from "./pages/ImportPage";
import MarketingSwag from "./pages/MarketingSwag";
import Fleet from "./pages/Fleet";
import Evaluations from "./pages/Evaluations";
import EvaluationDetail from "./pages/EvaluationDetail";
import PublicEvaluationSign from "./pages/PublicEvaluationSign";
import PublicEvaluationCopy from "./pages/PublicEvaluationCopy";
import PublicCredentialReturn from "./pages/PublicCredentialReturn";
import LicenseExpirationsPage from "./pages/LicenseExpirationsPage";
import GSOCLog from "./pages/GSOCLog";
import GSOCEntry from "./pages/GSOCEntry";
import GSOCConfig from "./pages/GSOCConfig";
import GSOCIntegrity from "./pages/GSOCIntegrity";
import GSOCReports from "./pages/GSOCReports";
import GSOCPassOn from "./pages/GSOCPassOn";
import GSOCPassOnAudit from "./pages/GSOCPassOnAudit";
import GSOCSOP from "./pages/GSOCSOP";
import GSOCContacts from "./pages/GSOCContacts";
import Announcements from "./pages/Announcements";
import WorkOrders from "./pages/WorkOrders";
import CompanyLicenses from "./pages/CompanyLicenses";
import OfficeLocations from "./pages/OfficeLocations";

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AuthProvider>
        <RouterProvider router={router} />
        {AI_ASSISTANT_ENABLED && <AIChatWidget />}
        <Toaster position="top-center" richColors />
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
export default App;

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/emergency-access" element={<EmergencyAccess />} />
          <Route path="/public/evaluations/copy/:token" element={<PublicEvaluationCopy />} />
          <Route path="/public/evaluations/:token" element={<PublicEvaluationSign />} />
          <Route path="/public/credentials/return/:token" element={<PublicCredentialReturn />} />

          {/* Protected routes */}
          <Route path="/" element={<Layout />} errorElement={<RouteError />}>
            <Route index element={<Dashboard />} />

            {/* AssetHub › Assets (tabbed) */}
            <Route path="items" element={<TabbedPage itemId="assets"><Items /></TabbedPage>} />
            <Route path="items/:id" element={<ItemDetail />} />
            <Route path="inventory" element={<TabbedPage itemId="assets"><Inventory /></TabbedPage>} />
            <Route path="locations" element={<TabbedPage itemId="assets"><Locations /></TabbedPage>} />
            <Route path="locations/:id/dashboard" element={<LocationDashboard />} />

            {/* AssetHub › Requisitions (tabbed) */}
            <Route path="requests" element={<TabbedPage itemId="requisitions"><Requests /></TabbedPage>} />
            <Route path="requests/:id" element={<RequestDetail />} />
            <Route path="asset-checkouts" element={<TabbedPage itemId="requisitions"><AssetCheckouts /></TabbedPage>} />

            {/* AssetHub › plain */}
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route path="purchase-orders/:id" element={<PODetail />} />
            <Route path="fleet" element={<Fleet />} />
            <Route path="marketing-swag" element={<MarketingSwag />} />

            {/* People */}
            <Route path="employees" element={<Employees />} />
            <Route path="employees/:id" element={<EmployeeDetail />} />
            <Route path="licenses/expiring" element={<LicenseExpirationsPage />} />
            <Route path="supervisory-groups" element={<SupervisoryGroups />} />
            <Route path="evaluations" element={<TabbedPage itemId="evaluations"><Evaluations /></TabbedPage>} />
            <Route path="evaluations/:id" element={<EvaluationDetail />} />
            <Route path="evaluation-completion" element={<TabbedPage itemId="evaluations"><EvaluationCompletion /></TabbedPage>} />

            {/* Clients & Jobs */}
            <Route path="my-accounts" element={<MyAccounts />} />
            <Route path="clients" element={<Clients />} />
            <Route path="sites" element={<Sites />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="work-orders" element={<WorkOrders />} />

            {/* Scheduling */}
            <Route path="scheduling" element={<CapGate module="scheduling"><Scheduling /></CapGate>} />
            <Route path="timesheets" element={<CapGate module="scheduling"><Timesheets /></CapGate>} />
            <Route path="scheduling/:jobId/:weekStart" element={<CapGate module="scheduling"><Scheduling /></CapGate>} />
            <Route path="scheduling/officer/:employeeId/:weekStart" element={<CapGate module="scheduling_grid"><Scheduling /></CapGate>} />

            {/* Finance › Payroll Review (payroll grant + Finance + admin) */}
            <Route path="scheduling/pay-rate-review" element={<CapGate hrGrant="payroll" roles={['finance']}><TabbedPage itemId="payroll-review"><PayRateReviewQueue /></TabbedPage></CapGate>} />
            <Route path="scheduling/self-approved" element={<CapGate hrGrant="payroll" roles={['finance']}><TabbedPage itemId="payroll-review"><SelfApprovedReport /></TabbedPage></CapGate>} />

            {/* Finance › Billing Setup (tabbed) */}
            <Route path="billing-items" element={<TabbedPage itemId="billing-setup"><BillingItems /></TabbedPage>} />
            <Route path="admin/hours-types" element={<TabbedPage itemId="billing-setup"><HoursTypes /></TabbedPage>} />

            {/* Finance › Vendors */}
            <Route path="vendors" element={<Vendors />} />

            {/* Finance › General Ledger (tabbed, each tab its own URL) */}
            <Route path="general-ledger" element={<Navigate to="/general-ledger/accounts" replace />} />
            <Route path="general-ledger/accounts" element={<CapGate module="ledger"><TabbedPage itemId="general-ledger"><GeneralLedger tab="accounts" /></TabbedPage></CapGate>} />
            <Route path="general-ledger/trial-balance" element={<CapGate module="ledger"><TabbedPage itemId="general-ledger"><GeneralLedger tab="trial-balance" /></TabbedPage></CapGate>} />
            <Route path="general-ledger/journal-entries" element={<CapGate module="ledger"><TabbedPage itemId="general-ledger"><GeneralLedger tab="journal-entries" /></TabbedPage></CapGate>} />
            <Route path="general-ledger/chart-import" element={<CapGate module="ledger"><TabbedPage itemId="general-ledger"><GeneralLedger tab="chart-import" /></TabbedPage></CapGate>} />
            <Route path="general-ledger/opening-balances" element={<CapGate module="ledger"><TabbedPage itemId="general-ledger"><GeneralLedger tab="opening-balances" /></TabbedPage></CapGate>} />

            {/* Finance › Payroll Parallel Test (v4.35.0, PREVIEW) — WinTeam SAR */}
            <Route path="payroll-sar" element={<Navigate to="/payroll-sar/import" replace />} />
            <Route path="payroll-sar/import" element={<CapGate module="payroll_import"><TabbedPage itemId="payroll-sar"><PayrollSAR tab="import" /></TabbedPage></CapGate>} />
            <Route path="payroll-sar/setup" element={<CapGate module="payroll_import"><TabbedPage itemId="payroll-sar"><PayrollSAR tab="setup" /></TabbedPage></CapGate>} />
            <Route path="payroll-sar/overtime" element={<CapGate module="payroll_import"><TabbedPage itemId="payroll-sar"><PayrollSAR tab="overtime" /></TabbedPage></CapGate>} />
            <Route path="payroll-sar/hours" element={<CapGate module="payroll_import"><TabbedPage itemId="payroll-sar"><PayrollSAR tab="hours" /></TabbedPage></CapGate>} />
            <Route path="payroll-sar/gross-pay" element={<CapGate module="payroll_import"><TabbedPage itemId="payroll-sar"><PayrollSAR tab="gross-pay" /></TabbedPage></CapGate>} />
            <Route path="payroll-net-pay" element={<CapGate module="payroll_import"><PayrollNetPay /></CapGate>} />

            {/* Administration › Users & Permissions (tabbed) */}
            <Route path="users" element={<TabbedPage itemId="users-permissions"><Users /></TabbedPage>} />
            <Route path="permissions" element={<TabbedPage itemId="users-permissions"><Permissions /></TabbedPage>} />

            {/* Administration › Settings (tabbed) */}
            <Route path="admin/evaluation-settings" element={<TabbedPage itemId="admin-settings"><EvaluationSettings /></TabbedPage>} />
            <Route path="admin/alert-recipients" element={<TabbedPage itemId="admin-settings"><AlertRecipients /></TabbedPage>} />

            {/* Administration › System (tabbed) */}
            <Route path="admin/system-status" element={<CapGate module="admin"><TabbedPage itemId="admin-system"><SystemStatus /></TabbedPage></CapGate>} />
            <Route path="admin/employee-merge" element={<CapGate module="admin"><TabbedPage itemId="admin-system"><EmployeeMerge /></TabbedPage></CapGate>} />
            <Route path="admin/diagnostics" element={<TabbedPage itemId="admin-system"><Diagnostics /></TabbedPage>} />
            <Route path="admin/email-delivery" element={<TabbedPage itemId="admin-system"><EmailDelivery /></TabbedPage>} />

            {/* Administration › Data & Audit (tabbed) */}
            <Route path="import" element={<TabbedPage itemId="admin-data"><ImportPage /></TabbedPage>} />
            <Route path="audit-logs" element={<TabbedPage itemId="admin-data"><AuditLogs /></TabbedPage>} />

            {/* Intelligence */}
            <Route path="reports" element={<Reports />} />            <Route path="forecasting" element={<Forecasting />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="help" element={<HelpCenter />} />
            {/* AI assistant parked 2026-09-08 — see lib/features.js */}
            <Route path="ai-assistant" element={AI_ASSISTANT_ENABLED ? <AIAssistant /> : <Navigate to="/" replace />} />

            {/* GSOC — every route capability-gated */}
            <Route path="gsoc" element={<CapGate module="gsoc"><GSOCLog /></CapGate>} />
            <Route path="gsoc/new" element={<CapGate module="gsoc" action="add"><GSOCEntry /></CapGate>} />
            <Route path="gsoc/pass-on" element={<CapGate module="gsoc"><TabbedPage itemId="gsoc-passon"><GSOCPassOn /></TabbedPage></CapGate>} />
            <Route path="gsoc/pass-on/audit" element={<CapGate module="gsoc_passon"><TabbedPage itemId="gsoc-passon"><GSOCPassOnAudit /></TabbedPage></CapGate>} />
            <Route path="gsoc/sop" element={<CapGate module="gsoc"><GSOCSOP /></CapGate>} />
            <Route path="gsoc/contacts" element={<CapGate module="gsoc"><GSOCContacts /></CapGate>} />
            <Route path="gsoc/reports" element={<CapGate module="gsoc_reports"><GSOCReports /></CapGate>} />
            <Route path="gsoc/config" element={<CapGate module="gsoc_config"><TabbedPage itemId="gsoc-settings"><GSOCConfig /></TabbedPage></CapGate>} />
            <Route path="gsoc/integrity" element={<CapGate module="gsoc_integrity"><TabbedPage itemId="gsoc-settings"><GSOCIntegrity /></TabbedPage></CapGate>} />

            {/* Compliance › Company Licenses (v4.38.0) */}
            <Route path="compliance/licenses" element={<CapGate module="compliance_licenses"><CompanyLicenses /></CapGate>} />
            <Route path="places/offices" element={<OfficeLocations />} />

            {/* Aliases / misc (old links preserved) */}
            <Route path="receiving" element={<PurchaseOrders />} />
            <Route path="returns" element={<Requests />} />
            <Route path="notifications" element={<Dashboard />} />
            <Route path="whats-new" element={<WhatsNew />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
    </>
  )
);
